import {
    DataTable,
    DateField,
    EditButton,
    NumberField,
    ReferenceField,
    ReferenceManyField,
    Show,
    TextField
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, ClipboardList, FileText, User, Wrench, ArrowLeft } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';

const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/design_requests" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4" /> Back to Design Requests
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight text-slate-900 ml-1">{record.reference}</span>
        </div>
    );
};
import { StatusBadge, StatusBadgeField } from '../documents/shared/StatusBadge';
import type { DesignRequest } from '../types';
import { ApproveDesignButton } from './ApproveDesignButton';
import { CreateDesignBoMButton } from './CreateDesignBoMButton';
import { DesignApprovalWorkflow } from './DesignApprovalWorkflow';
import { DesignRevisionComments } from './DesignRevisionComments';
import { DesignVersionHistory } from './DesignVersionHistory';

const DesignRequestShowActions = () => (
    <div className="flex items-center gap-2">
        <ApproveDesignButton />
        <CreateDesignBoMButton />
        <EditButton />
    </div>
);

const DesignRequestShowContent = () => {
    const record = useRecordContext<DesignRequest>();
    if (!record) return null;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            {/* Header Section */}
            <div className="flex justify-between items-start">
                <div className="flex gap-6 items-center">
                    <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center border-2 border-blue-100 shadow-sm">
                        <Wrench className="w-8 h-8 text-blue-500" />
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <h2 className="text-3xl font-bold tracking-tight text-slate-900">{record.reference}</h2>
                            <StatusBadge status={record.status} />
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                             <div className="font-semibold text-slate-900">{record.title}</div>
                             <span className="text-slate-300">|</span>
                             <ReferenceField source="project_id" reference="projects" link="show">
                                 <TextField source="name" className="text-primary hover:underline font-medium" />
                             </ReferenceField>
                        </div>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-3 mb-8">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="specifications">Specifications</TabsTrigger>
                    <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="md:col-span-2 space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                        <FileText className="w-4 h-4" /> Description
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="text-slate-700 leading-relaxed font-medium">
                                    {record.description || "No description provided."}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                        <ClipboardList className="w-4 h-4" /> Technical Scope
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <span className="text-xs font-bold text-slate-400 uppercase">Priority</span>
                                            <div>
                                                <Badge variant="outline" className="font-bold border-blue-200 text-blue-700 bg-blue-50 uppercase">
                                                    {record.priority}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="text-xs font-bold text-slate-400 uppercase">Due Date</span>
                                            <div className="flex items-center gap-2 font-semibold">
                                                <Calendar className="w-4 h-4 text-slate-400" />
                                                <DateField source="due_date" />
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="space-y-6">
                            <Card className="bg-slate-50 border-none shadow-none">
                                <CardHeader>
                                    <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500">Assignment</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border shadow-sm">
                                            <User className="w-5 h-5 text-slate-400" />
                                        </div>
                                        <div className="space-y-0.5">
                                            <span className="text-[10px] font-bold text-slate-400 uppercase">Designer</span>
                                            <div className="text-sm font-bold text-slate-900">
                                                <ReferenceField source="assigned_designer_id" reference="sales" link="show">
                                                    <TextField source="first_name" />
                                                </ReferenceField>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pt-4 border-t border-slate-200">
                                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-2">Requested On</div>
                                        <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
                                            <Calendar className="w-4 h-4" />
                                            <DateField source="requested_at" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <DesignApprovalWorkflow />
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="specifications">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader className="pb-3 border-b">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <Wrench className="w-3.5 h-3.5" /> Design Parameters
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6">
                                {record.specifications_jsonb ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {Object.entries(typeof record.specifications_jsonb === 'string' ? JSON.parse(record.specifications_jsonb as string) : record.specifications_jsonb).map(([key, value]) => (
                                            <div key={key} className="flex items-center justify-between py-3 border-b border-dashed border-slate-100">
                                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{key.replace(/_/g, ' ')}</span>
                                                <span className="text-sm font-semibold text-slate-900">{String(value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="py-8 text-center text-slate-400 text-sm">
                                        No specifications have been added yet. Use the Edit button to add design parameters.
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3 border-b bg-slate-50/50">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <ClipboardList className="w-3.5 h-3.5" /> Design Checklist
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="space-y-3">
                                    {['Materials selected', 'Dimensions confirmed', 'Cost estimated', 'Client approval obtained', 'BoM generated'].map((item) => (
                                        <label key={item} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors cursor-default">
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${record.status === 'approved' ? 'bg-emerald-100 border-emerald-400 text-emerald-600' : 'border-slate-300 text-transparent'}`}>
                                                ✓
                                            </div>
                                            <span className="text-sm text-slate-700 font-medium">{item}</span>
                                        </label>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
                
                <TabsContent value="history">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader className="pb-3 border-b">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <FileText className="w-3.5 h-3.5" /> Linked Bills of Materials
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <ReferenceManyField reference="boms" target="design_request_id">
                                    <DataTable bulkActionButtons={false} rowClick="show">
                                        <DataTable.Col source="reference" label="BoM Ref" className="font-mono font-bold text-blue-600" />
                                        <DataTable.Col source="version" label="Version" />
                                        <DataTable.Col source="status" label="Status">
                                            <StatusBadgeField source="status" />
                                        </DataTable.Col>
                                        <DataTable.Col source="total_estimated_cost" label="Est. Cost">
                                            <NumberField source="total_estimated_cost" options={{ style: 'currency', currency: 'INR' }} />
                                        </DataTable.Col>
                                    </DataTable>
                                </ReferenceManyField>
                            </CardContent>
                        </Card>

                        <DesignVersionHistory />
                        
                        <DesignRevisionComments />
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export const DesignRequestShow = () => (
    <Show title={<ShowTitle />} actions={<DesignRequestShowActions />}>
        <DesignRequestShowContent />
    </Show>
);
